import React, { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import MarkdownTable from './MarkdownTable'
import { parseTableLines } from './MarkdownTable'

type FAQItem = {
  q: string
  a: string
}

interface FAQAccordionProps {
  items: FAQItem[]
  answerClassName?: string
}

/** Render FAQ answer text, turning markdown tables into real tables. */
function FAQAnswer({ answer, className }: { answer: string; className: string }) {
  const lines = answer.split('\n')
  const blocks: React.ReactNode[] = []
  let i = 0

  while (i < lines.length) {
    const trimmed = lines[i].trim()

    // Consecutive table lines -> MarkdownTable
    if (trimmed.startsWith('|') && trimmed.includes('|')) {
      const tableLines = [lines[i]]
      let j = i + 1
      while (j < lines.length && lines[j].trim().startsWith('|')) {
        tableLines.push(lines[j])
        j++
      }
      if (parseTableLines(tableLines).length >= 2) {
        blocks.push(<MarkdownTable key={`table-${i}`} lines={tableLines} />)
        i = j
        continue
      }
    }

    if (trimmed) {
      blocks.push(
        <p key={i} className={`text-body pb-4 pl-0 pr-8 whitespace-pre-line ${className}`}>
          {trimmed}
        </p>
      )
    }
    i++
  }

  return <>{blocks}</>
}

const FAQAccordion: React.FC<FAQAccordionProps> = ({ items, answerClassName = 'text-sm sm:text-base' }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)

  return (
    <div className="max-w-3xl mx-auto space-y-1">
      {items.map((item, i) => (
        <div key={i} className="border-b border-neutral-200 dark:border-white/20">
          <button
            type="button"
            onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
            className="w-full py-3 sm:py-4 flex items-center justify-between gap-3 text-left min-h-[44px] hover:bg-neutral-50 dark:hover:bg-white/5 rounded-lg transition-colors"
            aria-expanded={openFaqIndex === i}
            aria-controls={`faq-answer-${i}`}
            id={`faq-question-${i}`}
          >
            <span className="text-heading font-medium text-sm sm:text-base">{item.q}</span>
            {openFaqIndex === i ? (
              <ChevronUp className="w-4 h-4 text-body flex-shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-body flex-shrink-0" />
            )}
          </button>
          <div
            id={`faq-answer-${i}`}
            role="region"
            aria-labelledby={`faq-question-${i}`}
            className={`overflow-hidden transition-all duration-200 ease-out ${
              openFaqIndex === i ? 'max-h-[64rem] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <FAQAnswer answer={item.a} className={answerClassName} />
          </div>
        </div>
      ))}
    </div>
  )
}

export default FAQAccordion
